import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Expand } from "lucide-react";

const FILTERS = ["All", "Interior", "Exterior", "Cabinet"];

export default function Gallery({ items }) {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const shown = items.filter((i) => filter === "All" || i.category === filter);

  const onSpotlight = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="work" className="relative py-24 sm:py-32" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }} className="font-mono text-xs tracking-[0.25em] uppercase text-sky mb-4">
              Recent Work
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Finishes that speak for themselves.
            </motion.h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                  filter === f ? "bg-royal text-white" : "border border-white/15 text-slate-300 hover:border-sky/50 hover:text-sky"
                }`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {shown.map((item) => (
              <motion.button key={item.id} layout
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.45 }}
                onClick={() => setActive(item)} onMouseMove={onSpotlight}
                className="group relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 text-left spotlight-card"
                data-testid={`gallery-item-${item.id}`}>
                <img src={item.src} alt={item.caption || "Painting project"} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-80" />
                <div className="absolute top-4 right-4 rounded-full bg-ink/60 backdrop-blur p-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[2]">
                  <Expand className="h-4 w-4 text-sky" />
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 z-[2]">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sky">{item.category}</p>
                  <p className="mt-1 font-display font-semibold">{item.caption}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
        {shown.length === 0 && (
          <p className="mt-12 text-center text-slate-500">No projects in this category yet.</p>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-4xl bg-navy border-white/10 p-2" data-testid="gallery-lightbox">
          {active && (
            <div>
              <img src={active.src} alt={active.caption} className="w-full max-h-[75vh] object-contain rounded-xl" />
              <p className="px-4 py-3 text-sm text-slate-300">{active.caption}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
